'use client';

import { useTranslations } from 'next-intl';
import {
  CheckCircle2,
  HelpCircle,
  XCircle,
  type LucideIcon
} from 'lucide-react';

import { cn } from '@/lib/utils';
import type { BatchPageRecord } from '../types';

// ─── Match tone resolution ───────────────────────────────────────────────────
// The "success match or not" state of a scanned page, derived from `qr_status`
// (does the page's printed QR match this container's expected sticking labels)
// with `status` as the fallback. Mirrors the mapping the QR grid used, so the
// thumbnail rail reads identically. Styled like the qr-review page sidebar:
// a full-cover tinted overlay + centered icon over the thumbnail, plus a
// colour-coded text label saying whether it matched.
export type PageMatchTone = 'matched' | 'mismatched' | 'unverified' | 'failed';

interface ToneStyle {
  Icon: LucideIcon;
  /** Translucent full-cover tint over the thumbnail. */
  tintClass: string;
  /** Centered overlay icon colour. */
  iconClass: string;
  /** Text-label colour. */
  textClass: string;
  /** Tile border colour. */
  borderClass: string;
}

const TONE: Record<PageMatchTone, ToneStyle> = {
  matched: {
    Icon: CheckCircle2,
    tintClass: 'bg-emerald-500/15',
    iconClass: 'text-emerald-600',
    textClass: 'text-emerald-600 dark:text-emerald-400',
    borderClass: 'border-emerald-500'
  },
  mismatched: {
    Icon: XCircle,
    tintClass: 'bg-rose-500/25',
    iconClass: 'text-rose-600',
    textClass: 'text-rose-600 dark:text-rose-400',
    borderClass: 'border-rose-500'
  },
  failed: {
    Icon: XCircle,
    tintClass: 'bg-rose-500/25',
    iconClass: 'text-rose-600',
    textClass: 'text-rose-600 dark:text-rose-400',
    borderClass: 'border-rose-500'
  },
  unverified: {
    Icon: HelpCircle,
    tintClass: 'bg-amber-500/25',
    iconClass: 'text-amber-600',
    textClass: 'text-amber-700 dark:text-amber-400',
    borderClass: 'border-rose-500'
  }
};

export function resolvePageMatchTone(page: BatchPageRecord): PageMatchTone {
  if (page.qr_status === 'mismatched') return 'mismatched';
  if (page.qr_status === 'unverified') return 'unverified';
  if (page.qr_status === 'success' || page.status === 'success') {
    return 'matched';
  }
  if (page.status === 'failed') return 'failed';
  return 'unverified';
}

/** Border colour for the given page's match tone — success (green), error
 * (red), or unverified (amber). */
export function resolvePageMatchBorderClass(page: BatchPageRecord): string {
  return TONE[resolvePageMatchTone(page)].borderClass;
}

// ─── Overlay (over the thumbnail) ────────────────────────────────────────────
/**
 * Full-cover tinted overlay with a centered match icon — the "success match or
 * not" indicator drawn over a page thumbnail (qr-review style). Non-interactive
 * so clicks fall through to the thumbnail's select handler.
 */
export function PageMatchOverlay({
  page,
  className
}: {
  page: BatchPageRecord;
  className?: string;
}) {
  const { Icon, tintClass, iconClass } = TONE[resolvePageMatchTone(page)];
  return (
    <div
      className={cn(
        'pointer-events-none absolute inset-0 flex items-center justify-center rounded-md',
        tintClass,
        className
      )}
      aria-hidden
    >
      <Icon className={cn('h-7 w-7 drop-shadow-sm', iconClass)} />
    </div>
  );
}

// ─── Text label ──────────────────────────────────────────────────────────────
/**
 * Colour-coded text saying whether the page matched — "Matched" / "Wrong Page"
 * / "QR not read" / "Not matched".
 */
export function PageMatchLabel({
  page,
  className
}: {
  page: BatchPageRecord;
  className?: string;
}) {
  const t = useTranslations('photoScanning');
  const tone = resolvePageMatchTone(page);
  const text =
    tone === 'matched'
      ? t('matchedLabel')
      : tone === 'mismatched'
        ? t('mismatchLabel')
        : tone === 'unverified'
          ? t('unverifiedLabel')
          : t('notMatchedLabel');
  return (
    <span
      className={cn(
        'text-[10px] font-semibold',
        TONE[tone].textClass,
        className
      )}
    >
      {text}
    </span>
  );
}
